import React, { useState } from 'react'
import { Shield, User, Mail, Building2, Trash2, Download, Eye, XCircle, CheckCircle, Loader, AlertTriangle, Info } from 'lucide-react'
import { useTenant } from '../contexts/TenantContext'

type RequestType = 'access' | 'delete' | 'portability' | 'rectification' | 'restriction' | 'objection' | 'withdraw-consent'

interface PrivacyRequestForm {
  requestType: RequestType
  email: string
  name: string
  company: string
  description: string
  verificationMethod: 'email' | 'id-document'
}

const PrivacyPortalPage: React.FC = () => {
  const { config } = useTenant()
  const [step, setStep] = useState<'form' | 'verification' | 'success'>('form')
  const [formData, setFormData] = useState<PrivacyRequestForm>({
    requestType: 'access',
    email: '',
    name: '',
    company: '',
    description: '',
    verificationMethod: 'email'
  })
  const [verificationCode, setVerificationCode] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [requestId, setRequestId] = useState('')

  const requestTypes = [
    {
      id: 'access' as const,
      icon: Eye,
      title: 'Access My Data',
      description: 'Request a copy of your personal data we hold',
      law: 'GDPR Art. 15 / CCPA §1798.100'
    },
    {
      id: 'delete' as const,
      icon: Trash2,
      title: 'Delete My Data',
      description: 'Request deletion of your personal data (Right to be Forgotten)',
      law: 'GDPR Art. 17 / CCPA §1798.105'
    },
    {
      id: 'portability' as const,
      icon: Download,
      title: 'Data Portability',
      description: 'Receive your data in a structured, machine-readable format',
      law: 'GDPR Art. 20'
    },
    {
      id: 'rectification' as const,
      icon: User,
      title: 'Correct My Data',
      description: 'Request correction of inaccurate personal data',
      law: 'GDPR Art. 16 / CCPA §1798.106'
    },
    {
      id: 'restriction' as const,
      icon: XCircle,
      title: 'Restrict Processing',
      description: 'Limit how we process your personal data',
      law: 'GDPR Art. 18'
    },
    {
      id: 'objection' as const,
      icon: AlertTriangle,
      title: 'Object to Processing',
      description: 'Object to processing of your personal data',
      law: 'GDPR Art. 21'
    },
    {
      id: 'withdraw-consent' as const,
      icon: XCircle,
      title: 'Withdraw Consent',
      description: 'Withdraw previously given consent',
      law: 'GDPR Art. 7(3)'
    }
  ]

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)

    try {
      const response = await fetch('/api/privacy/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          tenantId: config.tenantId,
          version: config.version
        })
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to submit request')
      }

      const data = await response.json()
      setRequestId(data.requestId)
      setStep('verification')
    } catch (err) {
      console.error('Privacy request submission failed:', err)
      setError(err instanceof Error ? err.message : 'Failed to submit request')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)

    try {
      const response = await fetch('/api/privacy/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requestId,
          code: verificationCode,
          tenantId: config.tenantId,
          version: config.version
        })
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Invalid verification code')
      }

      setStep('success')
    } catch (err) {
      console.error('Privacy verification failed:', err)
      setError(err instanceof Error ? err.message : 'Verification failed')
    } finally {
      setIsSubmitting(false)
    }
  }

  const selectedRequestType = requestTypes.find(rt => rt.id === formData.requestType)

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded flex items-center justify-center">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Privacy Center</h1>
                <p className="text-sm text-gray-600">Exercise Your Privacy Rights</p>
              </div>
            </div>
            <a href="/" className="text-primary-600 hover:text-primary-700 text-sm">
              ← Back to Trust Center
            </a>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <div className="flex items-start">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm text-blue-900">
              <p className="font-semibold mb-2">Your Privacy Rights</p>
              <p className="text-blue-800">
                Under GDPR, CCPA, and other privacy laws, you have the right to access, correct, delete,
                and control how your personal data is used. We take these rights seriously and will respond
                to your request within 30 days (or 45 days under CCPA).
              </p>
            </div>
          </div>
        </div>

        {step === 'form' && (
          <>
            {/* Request Type Selection */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">1. Select Request Type</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {requestTypes.map((type) => {
                  const IconComponent = type.icon
                  return (
                    <button
                      key={type.id}
                      onClick={() => setFormData({ ...formData, requestType: type.id })}
                      className={`text-left p-4 border-2 rounded-lg transition-all ${
                        formData.requestType === type.id
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <IconComponent className={`h-6 w-6 mt-1 ${
                          formData.requestType === type.id ? 'text-primary-600' : 'text-gray-400'
                        }`} />
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">{type.title}</h3>
                          <p className="text-sm text-gray-600 mb-2">{type.description}</p>
                          <p className="text-xs text-gray-500">{type.law}</p>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Request Form */}
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">2. Provide Your Information</h2>

              {/* Selected Request Info */}
              {selectedRequestType && (
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <selectedRequestType.icon className="h-5 w-5 text-primary-600 mt-0.5 mr-3" />
                    <div>
                      <h3 className="font-semibold text-primary-900">{selectedRequestType.title}</h3>
                      <p className="text-sm text-primary-800 mt-1">{selectedRequestType.description}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="john@company.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company/Organization (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Company Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Details (Optional)
                  </label>
                  <textarea
                    rows={4}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Provide any additional context or specific information about your request..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verification Method <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="verification"
                        value="email"
                        checked={formData.verificationMethod === 'email'}
                        onChange={(e) => setFormData({ ...formData, verificationMethod: 'email' })}
                        className="mr-3"
                      />
                      <div>
                        <p className="font-medium text-gray-900">Email Verification</p>
                        <p className="text-sm text-gray-600">We'll send a verification code to your email</p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {error && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div className="mt-6 flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  By submitting, you confirm that you are the data subject or authorized to act on their behalf.
                </p>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isSubmitting ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin mr-2" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Request'
                  )}
                </button>
              </div>
            </form>
          </>
        )}

        {step === 'verification' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Identity</h2>
              <p className="text-gray-600">
                We've sent a verification code to <strong>{formData.email}</strong>
              </p>
            </div>

            <form onSubmit={handleVerification} className="max-w-md mx-auto">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  required
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-center text-2xl tracking-widest"
                  placeholder="000000"
                  maxLength={6}
                />
              </div>

              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting || verificationCode.length !== 6}
                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin mr-2" />
                    Verifying...
                  </>
                ) : (
                  'Verify & Submit Request'
                )}
              </button>

              <p className="text-center text-sm text-gray-500 mt-4">
                Didn't receive the code? Check your spam folder or{' '}
                <button type="button" className="text-primary-600 hover:text-primary-700">
                  resend code
                </button>
              </p>
            </form>
          </div>
        )}

        {step === 'success' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Request Submitted Successfully</h2>
              <p className="text-gray-600 mb-6">
                Your privacy request has been received and is being processed.
              </p>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-gray-700 mb-2">Request ID</p>
                <p className="text-lg font-mono text-gray-900">{requestId}</p>
              </div>

              <div className="text-left bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-blue-900 mb-2">What Happens Next?</h3>
                <ul className="text-sm text-blue-800 space-y-2">
                  <li className="flex items-start">
                    <span className="mr-2">1.</span>
                    <span>We will review your request within 1 business day</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">2.</span>
                    <span>You'll receive email updates on the status of your request</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">3.</span>
                    <span>We will fulfill your request within 30 days (GDPR) or 45 days (CCPA)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">4.</span>
                    <span>If we need additional information, we'll contact you via email</span>
                  </li>
                </ul>
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={() => {
                    setStep('form')
                    setFormData({
                      requestType: 'access',
                      email: '',
                      name: '',
                      company: '',
                      description: '',
                      verificationMethod: 'email'
                    })
                    setVerificationCode('')
                    setRequestId('')
                  }}
                  className="flex-1 btn-secondary"
                >
                  Submit Another Request
                </button>
                <a href="/" className="flex-1 btn-primary">
                  Return to Trust Center
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PrivacyPortalPage
